import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Chip, Divider, Paper, Stack, Typography } from '@mui/material';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import {
  buildCertificationId,
  type AcademicLevel,
  type CapCertificationSummary,
  type CapCode,
  type CapSubmissionSummary,
} from '@dosfilos/domain';
import { db } from '../../../lib/firebase';
import { formatDate } from '../../../lib/i18n-format';

// ─── Lo que TMAI respondió, del lado del centro (CAP Fase 2) ────────────────
//
// Presentar una propuesta escribe dos cosas en la misma transacción: la ronda en
// la red y su espejo en el centro (`capProposalSubmissions`). Cuando el revisor
// decide, el espejo recibe el estado, la fecha y las observaciones; al aprobar,
// el centro recibe además la copia de la certificación (`capCertifications`).
// Hasta acá NINGUNA pantalla leía ese espejo: un centro al que TMAI le devolvía
// la propuesta seguía viendo "Sometida" con el botón "Presentar" activo, sin
// rastro de lo que le pidieron. Las observaciones son el canal de retorno de la
// red; sin verlas, responder es adivinar.
//
// Lectura directa a Firestore: las reglas permiten leer ambas colecciones a
// tenantAdmin y registrar. Otro rol recibe permission-denied y el panel no se
// muestra — el resto de la pantalla sigue funcionando.

export interface CapNetworkReview {
  /** Rondas de este programa + CAP + nivel, de la más reciente a la más antigua. */
  rounds: CapSubmissionSummary[];
  certification: CapCertificationSummary | null;
  /** false si no se pudo leer (rol sin acceso o sin red): la pantalla no afirma nada. */
  available: boolean;
  reload: () => Promise<void>;
}

export function useCapNetworkReview(
  tenantId: string | null | undefined,
  programId: string | undefined,
  capCode: CapCode,
  level: AcademicLevel,
): CapNetworkReview {
  const [rounds, setRounds] = useState<CapSubmissionSummary[]>([]);
  const [certification, setCertification] = useState<CapCertificationSummary | null>(null);
  const [available, setAvailable] = useState(false);

  const reload = useCallback(async () => {
    if (!tenantId || !programId) return;
    try {
      // Filtro de un solo campo (sin índice compuesto); CAP y nivel se filtran acá.
      const snap = await getDocs(
        query(
          collection(db, `tenants/${tenantId}/capProposalSubmissions`),
          where('programId', '==', programId),
        ),
      );
      const own = snap.docs
        .map((d) => d.data() as CapSubmissionSummary)
        .filter((r) => r.capCode === capCode && r.level === level)
        .sort((a, b) => b.round - a.round);
      const cert = await getDoc(
        doc(
          db,
          `tenants/${tenantId}/capCertifications/${buildCertificationId(programId, capCode, level)}`,
        ),
      );
      setRounds(own);
      setCertification(cert.exists() ? (cert.data() as CapCertificationSummary) : null);
      setAvailable(true);
    } catch {
      setRounds([]);
      setCertification(null);
      setAvailable(false);
    }
  }, [tenantId, programId, capCode, level]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { rounds, certification, available, reload };
}

export const REVIEW_STATUS_COLOR: Record<
  CapSubmissionSummary['status'],
  'default' | 'info' | 'warning' | 'success' | 'error'
> = {
  submitted: 'info',
  returned: 'warning',
  approved: 'success',
  rejected: 'error',
  withdrawn: 'default',
  superseded: 'default',
};

const GUIDANCE_SEVERITY: Record<
  CapSubmissionSummary['status'],
  'info' | 'warning' | 'success' | 'error'
> = {
  submitted: 'info',
  returned: 'warning',
  approved: 'success',
  rejected: 'error',
  withdrawn: 'info',
  superseded: 'info',
};

export function CapNetworkReviewPanel({
  review,
  currentProposalId,
}: {
  review: CapNetworkReview;
  currentProposalId: string | undefined;
}) {
  const { t } = useTranslation('cap');
  const { rounds, certification, available } = review;
  if (!available || (rounds.length === 0 && !certification)) return null;

  const date = (iso: string) => formatDate(iso, { dateStyle: 'long' });
  const [latest, ...earlier] = rounds;

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 2, borderRadius: 3 }}>
      <Typography variant="subtitle1" fontWeight={700} mb={1.5}>
        {t('proposal.review.title')}
      </Typography>

      {certification?.status === 'granted' && (
        // Otorgada con condiciones se ve DISTINTO de otorgada limpia. Y si el
        // centro no ve qué debe y para cuándo, no hay ninguna posibilidad de
        // que lo cumpla — la condición sería sólo una nota interna de TMAI.
        <Alert
          severity={certification.conditions ? 'warning' : 'success'}
          sx={{ mb: latest ? 2 : 0 }}
        >
          {t(
            certification.conditions
              ? 'proposal.review.certifiedWithConditions'
              : 'proposal.review.certified',
            {
              cap: t(`plos.caps.${certification.capCode}`),
              level: t(`levels.${certification.level}`),
              date: date(certification.grantedAt),
              reviewBy: certification.conditions ? date(certification.conditions.reviewBy) : '',
            },
          )}
          {certification.conditions && (
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
              {certification.conditions.items.map((c, i) => (
                <li key={i}>
                  <Typography variant="body2">
                    {t(`dossier.findings.${c.code}`, {
                      area: c.area ? t(`subjectAreas.${c.area}`) : '',
                    })}
                  </Typography>
                  {c.note && (
                    <Typography variant="body2" color="text.secondary">
                      {c.note}
                    </Typography>
                  )}
                </li>
              ))}
            </Box>
          )}
        </Alert>
      )}
      {certification?.status === 'revoked' && (
        <Alert severity="error" sx={{ mb: latest ? 2 : 0 }}>
          {t('proposal.review.revoked', {
            date: certification.revokedAt ? date(certification.revokedAt) : '—',
          })}
          {certification.revokedReason && (
            <Box component="span" sx={{ display: 'block', mt: 0.5, whiteSpace: 'pre-wrap' }}>
              {certification.revokedReason}
            </Box>
          )}
        </Alert>
      )}

      {latest && (
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" mb={1}>
            <Typography fontWeight={600}>
              {t('proposal.review.roundLine', {
                round: latest.round,
                kind: t(`proposal.review.kind.${latest.submissionKind}`),
                version: latest.proposalVersion,
              })}
            </Typography>
            <Chip
              size="small"
              color={REVIEW_STATUS_COLOR[latest.status]}
              label={t(`proposal.review.status.${latest.status}`)}
            />
            {currentProposalId && latest.proposalId !== currentProposalId && (
              <Chip size="small" variant="outlined" label={t('proposal.review.otherVersion')} />
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {t('proposal.review.submittedOn', { date: date(latest.submittedAt) })}
            {latest.decidedAt
              ? ` · ${t('proposal.review.decidedOn', { date: date(latest.decidedAt) })}`
              : ''}
          </Typography>

          <Alert severity={GUIDANCE_SEVERITY[latest.status]} sx={{ mt: 1.5 }}>
            {t(
              `proposal.review.guidance.${
                latest.status === 'returned' && latest.submissionKind === 'advisory'
                  ? 'returnedAdvisory'
                  : latest.status
              }`,
            )}
          </Alert>

          {/* La voz del revisor, entera y sin tocar: resumirla dejaría al centro
              adivinando qué corregir. */}
          {latest.observations && (
            <Box
              sx={{
                mt: 1.5,
                p: 1.5,
                borderLeft: '3px solid',
                borderLeftColor: 'warning.main',
                bgcolor: 'action.hover',
                borderRadius: 1,
              }}
            >
              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                {t('proposal.review.observations')}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                {latest.observations}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {earlier.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" fontWeight={700} mb={1}>
            {t('proposal.review.earlier')}
          </Typography>
          <Stack divider={<Divider />}>
            {earlier.map((r) => (
              <Box key={r.id} sx={{ py: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="body2" fontWeight={600}>
                    {t('proposal.review.roundLine', {
                      round: r.round,
                      kind: t(`proposal.review.kind.${r.submissionKind}`),
                      version: r.proposalVersion,
                    })}
                  </Typography>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={t(`proposal.review.status.${r.status}`)}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {date(r.decidedAt ?? r.submittedAt)}
                  </Typography>
                </Stack>
                {r.observations && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}
                  >
                    {r.observations}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        </>
      )}
    </Paper>
  );
}
